/**
 * Building the customer quotation from the allocation, in the browser.
 *
 * ---- Why the client builds this ----
 *
 * `comparison.build_quotation` is the server's own get-or-create, and it cannot run:
 *
 *   POST comparison.build_quotation
 *     File "apps/quoflow/quoflow/quoflow/api/comparison.py", line 924, in build_quotation
 *       from quoflow.quoflow.quoflow.quoflow.api.quotation import get_quotation
 *     ModuleNotFoundError: No module named 'quoflow.quoflow.quoflow.quoflow'
 *
 * FOUR `quoflow` segments where the module has three — the same one-too-many import as `B1`
 * (extraction) and `B2` (`rfq.py:71`), in a third place. It surfaced only once the allocation save
 * in front of it was fixed, because nothing had ever reached it before.
 *
 * `quotation.create_quotation` is healthy (probed with a bogus enquiry: `LinkValidationError`, i.e.
 * it reaches its own validation), takes the whole document including priced lines, and is what this
 * module targets instead.
 *
 * ---- What this means, and it is not small ----
 *
 * `CLAUDE.md` assigns "the persisted quotation total + rule enforcement on send" to the BACKEND.
 * Building the payload here moves the sell price into the client: cost × margin, per line, computed
 * in a browser and persisted as fact. That is a deliberate departure, taken because the alternative
 * is that no customer quotation can be produced at all.
 *
 * Two things follow, and both are load-bearing:
 *
 *  1. **The COST is carried verbatim from the allocation.** Nothing here re-derives what a supplier
 *     charges — that number came from their quotation and is only ever copied.
 *  2. **The margin is the server's own**, read from `settings.get_pricing_rules`
 *     (`category_margins`, else `default_markup_pct`), never a constant in this file. When the
 *     backend takes pricing back, the numbers do not move.
 *
 * ---- The blended cost ----
 *
 * One enquiry line can be split across several suppliers, and the customer sees ONE line. Its cost
 * is the weighted average of the split — `Σ(qty × rate) / Σ(qty)` — not the cheapest leg and not a
 * simple mean. This is the "blended average" the workbench used to display: the figure was removed
 * from the screen, the computation was kept precisely because this is what needs it.
 */

import { withoutInternalAnnotations } from './internalNotes'
import { sellFromMargin } from './pricing'
import { buildSourcingNote } from './sourcingNote'
import { describeRate, pickRate, type ExchangeRateRow } from './exchangeRates'
import type {
  AllocationRow,
  CategoryMargins,
  ComparisonSupplier,
  ItemCategory,
  Money,
  PricingRulesSettings,
} from './types'

/** One enquiry line, priced. Mirrors `create_quotation`'s documented `items[]` row. */
export interface QuotationLinePayload {
  item_code: string
  item_name: string
  /** The printed description, as edited on the RFQ page. Omitted when the line has none. */
  description?: string
  /** The through-key. Omitting it is what made a filed supplier quotation invisible — see
   *  `supplierQuotationFromWorkbook.ts`. Never dropped here for the same reason. */
  enquiry_row_no: string
  source: string
  qty: number
  uom: string
  sell_uom: string
  /** Weighted average of the split, in base currency. Copied from the allocation, never re-derived. */
  cost_unit: number
  default_margin_pct: number
  margin: number
  sell_unit: number
  unit_price: number
  amount: number
  selected_rate: number
  lowest_quote_price: number
  /**
   * `Link → Supplier` on both `Quotation Item` and `Quotation Line` (queried live 2026-09-03).
   * Only ever a real Supplier DOCNAME, or empty — never a display label and never a marker. Sending
   * `IN_HOUSE_SUPPLIER` here failed Frappe's link validation and took whole quotations down; see
   * `contractQuotation.ts`.
   */
  lowest_quote_supplier: string
  /**
   * `Small Text` on `Quotation Line` — what the supplier said about the offer that was accepted.
   * Omitted when they said nothing, so a blank never overwrites a value the server holds.
   */
  offer_remarks?: string

  /* ---- The four columns the bench does not have YET (Akhil's spec, `D22`) ----
   *
   * Probed 2026-09-03: sent with real values, returned 200, and all four were SILENTLY DROPPED —
   * only `source` and `offer_remarks` persisted. Frappe ignores an unknown field on a child row
   * without erroring, so sending them costs nothing today and means the day the schema catches up
   * is a deploy rather than a task. */

  /** The Supplier docname the PO will be raised against. `''` for in-house stock — nobody. */
  supplier?: string
  /** The supplier's own quoted currency. */
  po_currency?: string
  /**
   * The supplier's OWN quoted rate for a single unit, verbatim (Akhil, 2026-09-03: "based single
   * qty, we just need to pass what is given by the supplier"). NOT an exchange rate, despite the
   * name, and NOT the rate restated into this line's uom — see `AllocationRow.quoted_rate`.
   */
  po_currency_rate?: number
  /** The exact `Supplier Quotation` the accepted offer came from. */
  supplier_quotation?: string
  /**
   * The CURRENCY rate from `po_currency` to the company's — live-confirmed by the document's own
   * arithmetic: `po_currency_rate 1.63 (USD) × conversion_rate 3.67 = cost_unit 6.00 (AED)`.
   *
   * NOTE the collision: the field of this name on a COMPARISON offer is the UOM factor, a
   * completely different quantity (`useComparison.ts`'s `baseAmount` exists because of it). Same
   * name, two doctypes, two meanings — do not carry one into the other.
   */
  conversion_rate?: number
  /**
   * `Data`, free text — where provenance goes when it is not a Supplier. Carries "In-house stock"
   * for a line off our own shelf, which has no supplier to link to but is still worth recording.
   */
  supplier_note?: string
  /** `rfq` for a supplier-quoted line, `provision` for a contract / stock line — the route badge. */
  tag?: 'rfq' | 'provision'
}

export interface CreateQuotationPayload {
  status: string
  revision: number
  enquiry: string
  customer: string
  customer_name: string
  transaction_date: string
  customer_currency: string
  exchange_rate: number
  supplier_quote_comparison: string
  sales_person: string
  purchaser: string
  vessel: string
  delivery_location: string
  /** The `Port` docname — omitted when the enquiry has none (a blank Link is a validation error). */
  port?: string
  markup: number
  net_total: number
  total: number
  grand_total: number
  items: QuotationLinePayload[]
  /* ---- Filled by `completeQuotationPayload` (lib/quotationPayload.ts) for every creator ---- */
  /** Mandatory since 2026-09-17 — the enquiry's company. */
  company?: string
  /** Mandatory Select, defaults to "Sales" on the doctype. */
  order_type?: string
  /** Mandatory Link to the Enquiry Type master (server, 2026-09-17 pm) — the enquiry's own type. */
  enquiry_type?: string
  /** The company's currency. */
  currency?: string
  /** POs are raised in the company's currency: `po_currency` = company, `po_exchange_rate` = 1. */
  po_currency?: string
  po_exchange_rate?: number
  total_po_cost?: number
  customer_reference_no?: string
  apply_discount_on?: 'Grand Total' | 'Net Total'
  /** The rate every line was priced at, when they all share one. */
  line_markup?: number
}

/** The enquiry line a quotation line prices — name, unit and the customer's asked quantity. */
export interface QuotableLine {
  item_code: string
  item_name: string
  enquiry_row_no: string
  uom: string
  /** `Enquiry Item.description` — what prints under the name. Carried, never invented. */
  description?: string
  /**
   * What the CUSTOMER asked for. Only read for a line nobody allocated: an allocated line's
   * quantity is the sum of its allocations, which is the figure actually being bought.
   *
   * Without it an unpriced line would reach the quotation at qty 0, and the purchaser pricing it
   * would have to go back to the enquiry to find out how many were wanted.
   */
  qty?: number
  /** The item's category when one is known. Quoflow has no category concept (Q15), so this is
   *  usually absent and the margin falls back to `default_markup_pct`. */
  category?: ItemCategory
  /**
   * This line is priced by the CONTRACT path, so it must not also be zero-filled here.
   *
   * `ComparePage` appends `buildContractQuotationLines`' output to this payload — whichever screen
   * writes last has to produce the whole document, because `update_quotation` replaces `items`
   * wholesale. A contract line emitted here at zero AND appended priced reaches the customer
   * TWICE, once at 0.00: the duplicate reported 2026-09-04.
   *
   * Set only where the contract builder will actually emit the line — it skips a contract-routed
   * line with no rate (`contractQuotation.ts`'s own refusal), and such a line has nowhere else to
   * come from, so it still travels at zero.
   */
  pricedByContract?: boolean
}

export interface BuildQuotationInput {
  enquiryId: string
  customer: string
  customerName: string
  salesPerson: string
  purchaser: string
  vessel: string
  deliveryLocation: string
  /** The enquiry's port of delivery (`port_of_delivery`), `''` when none. */
  deliveryPort: string
  /** `"YYYY-MM-DD"`. */
  transactionDate: string
  /** For dating the exchange rate on screen. Injected so the result is testable. */
  today: Date
  /** The `Supplier Quote Comparison` this was allocated on — the document's own provenance. */
  comparisonName: string
  lines: QuotableLine[]
  allocations: AllocationRow[]
  /**
   * The comparison's supplier columns — carries each supplier's DISPLAY NAME and the
   * `Supplier Quotation` its column was built from. Used to record the full sourcing decision per
   * line (`lib/sourcingNote.ts`), which `lowest_quote_supplier` alone cannot hold for a split.
   * Optional so existing callers and tests are unaffected; absent means no note.
   */
  suppliers?: ComparisonSupplier[]
  settings: PricingRulesSettings
  /** The COMPANY's currency — what the allocation's costs are already in. */
  baseCurrency: string
  /**
   * The customer's own currency (`Customer.default_currency`). Empty when they have none on file,
   * in which case they are quoted in the company's currency and no conversion happens.
   *
   * Measured 2026-08-08: 190 of 1561 customers carry one, across SAR (69), AED (67), BHD (29) and
   * USD (25). So for most of those, quoting in the company's currency is quoting in the wrong one.
   */
  customerCurrency: string
  /** Every `Currency Exchange` row — `pickRate` chooses, and says how old its answer is. */
  rates: readonly ExchangeRateRow[]
}

export type BuildResult =
  | {
      ok: true
      payload: CreateQuotationPayload
      /** How the customer's currency was reached — shown next to the total, never hidden. */
      conversion: { from: string; to: string; rate: number; note: string }
    }
  | { ok: false; reason: string }

/** Weighted average unit cost of a split — `Σ(qty × rate) / Σ(qty)`. */
export function blendedCost(rows: readonly AllocationRow[]): number {
  const qty = rows.reduce((sum, r) => sum + r.allocated_qty, 0)
  if (qty <= 0) return 0
  return rows.reduce((sum, r) => sum + r.allocated_qty * r.rate, 0) / qty
}

/**
 * The margin for a line: the server's category margin where the category is known, else its
 * `default_markup_pct`. Never a literal in this file — see the module header.
 */
/**
 * The margin a line is BUILT at.
 *
 * ---- `default_markup_pct` is deliberately not read (2026-09-04) ----
 *
 * It used to be the fallback, so every line arrived at cost × 1.15 from a Settings field nobody
 * had touched. On the quotation that prompted this: cost 45.00 sold at 51.75, 34.00 at 39.10,
 * 20.00 at 23.00 — while the commission panel directly above read "markup 0%". Two markups on one
 * screen, and the one actually applied was the invisible one.
 *
 * The markup now comes from ONE place: the quotation's own commission panel, applied deliberately
 * with "Apply to every line". A line is built AT COST until somebody prices it.
 *
 * That leaves a fresh quotation at zero margin, which the point-25 send gate blocks. Intended: it
 * is a pricing decision the purchaser makes, not one a Settings field makes for them.
 *
 * An explicit CATEGORY margin still applies — that is a deliberate per-category rule, and not what
 * was asked to be removed. It is inert on this data anyway (`D23`).
 */
export function marginFor(category: ItemCategory | undefined, settings: PricingRulesSettings): number {
  const margins = settings.category_margins as CategoryMargins | undefined
  const byCategory = category !== undefined && margins ? margins[category] : undefined
  return byCategory ?? 0
}

/** Rates keep 6dp: enough that `po_currency_rate × conversion_rate` reproduces the cost to the cent. */
const roundRate = (n: number): number => Math.round(n * 1e6) / 1e6
const round2 = (n: number): number => Math.round(n * 100) / 100

export function buildQuotationPayload(input: BuildQuotationInput): BuildResult {
  /*
    EVERY enquiry line reaches the quotation — the unpriced ones at zero.

    This used to build only from allocations and refuse outright when there were none. A line
    nobody had priced simply vanished from the customer's copy and the numbering closed up behind
    it, so their item 7 became our item 6 and the two documents could no longer be read against
    each other. `QuotePage` carried a banner listing what had gone missing, which showed the hole
    without letting anyone quote into it.

    The refusal goes too: a quotation of entirely unpriced lines is a legitimate starting point now
    that a price can be typed on the quotation screen.
  */
  const allocated = input.allocations.filter((row) => row.allocated_qty > 0)

  // Grouped by the through-key, NOT `item_code`: one enquiry can name the same item on two lines,
  // and merging them would price two customer lines as one.
  const byRow = new Map<string, AllocationRow[]>()
  for (const row of allocated) {
    const list = byRow.get(row.enquiry_row_no) ?? []
    list.push(row)
    byRow.set(row.enquiry_row_no, list)
  }

  const linesByRow = new Map(input.lines.map((line) => [line.enquiry_row_no, line]))
  const orphan = [...byRow.keys()].find((rowNo) => !linesByRow.has(rowNo))
  if (orphan !== undefined) {
    // Pricing a line we cannot name would put an unidentifiable row in front of a customer.
    return { ok: false, reason: `An allocation references enquiry line ${orphan}, which is not on this enquiry.` }
  }

  /*
    The customer's currency, and the rate to reach it.

    Costs are in the COMPANY's currency (the allocation's). A customer with a different one is
    quoted in theirs — but only at a rate the business actually set. `pickRate` never inverts and
    never routes through a third currency, because the live table is not reciprocal (SAR->AED 0.973
    against AED->SAR 1.02), so a derived rate would be a number nobody chose.

    No rate means NO QUOTATION. Falling back to 1.0 would price a foreign quotation at par and look
    entirely correct on screen — the customer would simply be billed the wrong amount.
  */
  const quoteCurrency = input.customerCurrency.trim() || input.baseCurrency
  const picked = pickRate(input.rates, input.baseCurrency, quoteCurrency)
  if (picked === null) {
    return {
      ok: false,
      reason:
        `No exchange rate on file for ${input.baseCurrency} → ${quoteCurrency}, so this cannot be ` +
        `quoted in the customer's currency. Add the rate, or quote in ${input.baseCurrency}.`,
    }
  }

  /*
    The SUPPLIERS' currencies, into the company's — before any costing (corrected 2026-09-14).

    An allocation row's `rate` is in the supplier's currency (`AllocationRow.currency`), and
    `blendedCost` used to sum those raw — a $5.45 offer was costed as AED 5.45, and only the PO
    side of the line said otherwise. Every row is restated into the company's currency here, at
    a rate the business set (same `pickRate` rule as the customer currency below: no inversion,
    no routing), and a row whose currency has no rate refuses the build rather than costing at
    par. `quoted_rate` stays as the supplier wrote it, for the PO.
  */
  const inBase: AllocationRow[] = []
  for (const row of allocated) {
    const from = (row.currency || input.baseCurrency).trim().toUpperCase()
    // A row the comparison page already restated (`quoted_currency` set, `currency` = base) is
    // at par here and keeps its supplier currency for the PO through `quoted_currency`.
    const rate = from === input.baseCurrency.toUpperCase() ? 1 : (pickRate(input.rates, from, input.baseCurrency)?.rate ?? null)
    if (rate === null) {
      return {
        ok: false,
        reason:
          `No exchange rate on file for ${from} → ${input.baseCurrency}, so ${row.supplier}'s offer ` +
          `on row ${row.enquiry_row_no} cannot be costed. Add the rate, or allocate another offer.`,
      }
    }
    inBase.push({ ...row, rate: row.rate * rate, quoted_rate: row.quoted_rate ?? row.rate })
  }
  // Re-grouped from the converted rows — the map above was built to validate the through-keys.
  byRow.clear()
  for (const row of inBase) {
    const list = byRow.get(row.enquiry_row_no) ?? []
    list.push(row)
    byRow.set(row.enquiry_row_no, list)
  }

  const items: QuotationLinePayload[] = []
  /*
    Driven by the ENQUIRY's lines, in their own order — not by the allocation map. That is what
    keeps `enquiry_row_no` contiguous and in sequence: iterating allocations would emit only the
    priced rows, in whatever order they happened to be grouped.
  */
  for (const line of input.lines) {
    const rowNo = line.enquiry_row_no
    const rows = byRow.get(rowNo)

    if (!rows || rows.length === 0) {
      // Priced by the contract path and appended there — emitting it here too is the duplicate.
      if (line.pricedByContract) continue
      /*
        Nobody quoted this line. It travels at zero, carrying its number, its name and the
        customer's own quantity, so the purchaser can price it on the quotation screen.

        `lowest_quote_supplier` stays EMPTY: no supplier quoted it, and that field is a Link where
        an invented value fails validation and takes the whole document down (`D22`).
      */
      items.push({
        item_code: line.item_code,
        item_name: line.item_name,
        ...(line.description && withoutInternalAnnotations(line.description) ? { description: withoutInternalAnnotations(line.description) } : {}),
        enquiry_row_no: rowNo,
        source: 'Supplier',
        qty: line.qty ?? 0,
        uom: line.uom,
        sell_uom: line.uom,
        cost_unit: 0,
        default_margin_pct: marginFor(line.category, input.settings),
        margin: marginFor(line.category, input.settings),
        sell_unit: 0,
        unit_price: 0,
        amount: 0,
        selected_rate: 0,
        lowest_quote_price: 0,
        lowest_quote_supplier: '',
      })
      continue
    }
    const qty = rows.reduce((sum, r) => sum + r.allocated_qty, 0)
    const cost = round2(blendedCost(rows))
    const margin = marginFor(line.category, input.settings)
    // `sellFromMargin`, not `defaultSellUnit`: the latter looks the margin up BY CATEGORY, and a
    // line with no category (the usual case — Q15) would find none and be sold at cost. The margin
    // is already resolved above; this applies it.
    // Margin first, in the company's currency, THEN converted — so the margin percentage means what
    // it says regardless of what the customer is billed in.
    const sell = round2(sellFromMargin({ amount: cost, currency: input.baseCurrency }, margin).amount * picked.rate)
    // The same cost, in the quotation's currency — what the line actually stores (see `cost_unit`).
    const quoteCost = round2(cost * picked.rate)
    const cheapest = rows.reduce((best, r) => (r.rate < best.rate ? r : best), rows[0])
    const poRate = round2(cheapest.quoted_rate ?? cheapest.rate)

    // The cheapest single leg, for the quote screen's "what was available" column. Distinct from
    // `cost_unit`, which is the blended figure actually being charged against.
    const sourcingNote = buildSourcingNote(rows, input.suppliers ?? [])
    // No split (Akhil), so one row per line — but reduce rather than index, so a split that does
    // arrive contributes its remarks instead of silently dropping all but the first.
    const offerRemarks = rows
      .map((r) => (r.remark ?? '').trim())
      .filter((r) => r !== '')
      .join(' · ')

    items.push({
      item_code: line.item_code,
      item_name: line.item_name,
      enquiry_row_no: rowNo,
      ...(line.description && withoutInternalAnnotations(line.description) ? { description: withoutInternalAnnotations(line.description) } : {}),
      /*
        Akhil's vocabulary, 2026-09-03: `supplier` | `provision` | `base price`. This used to send
        `'rfq'`, which is none of the three — the ERP reads this field to decide how the line is
        bought, and a value outside the vocabulary is one it cannot act on. Nothing in this app
        reads it back (`toQuotationLine` sets `source: ''`), so the change is invisible here.
      */
      source: 'Supplier',
      qty,
      uom: line.uom,
      sell_uom: line.uom,
      /*
        In the QUOTATION's currency, like the sell beside it (corrected 2026-09-14). The server
        derives `sell_unit` as `cost_unit × (1 + margin/100)` and ignores the sell we send, so a
        cost left in the company's currency makes every foreign-currency quotation wrong by the
        exchange rate — see `quotationCurrency.ts`. What the supplier actually charges is kept in
        `po_currency_rate` below, unconverted.
      */
      cost_unit: quoteCost,
      default_margin_pct: margin,
      margin,
      sell_unit: sell,
      unit_price: sell,
      amount: round2(sell * qty),
      selected_rate: quoteCost,
      lowest_quote_price: cheapest.rate,
      lowest_quote_supplier: cheapest.supplier,
      /*
        Point 22 — the whole sourcing decision, in the one field that can hold it.

        `lowest_quote_supplier` above is a single Link, so on a line split 60/40 across two
        suppliers it records only the cheaper one and the other leg vanishes. There is no RFQ or
        Supplier Quotation field on `Quotation Line` at all (checked live; asked for as `D22`), so
        until there is, the salesperson raising a PO reads it here.
      */
      ...(sourcingNote ? { supplier_note: sourcingNote } : {}),
      // Only when there is one — see the field's own note.
      ...(offerRemarks ? { offer_remarks: offerRemarks } : {}),
      // No split, so the single allocated row IS the sourcing decision. `cheapest` is that row.
      supplier: cheapest.supplier,
      // The SUPPLIER's currency — `quoted_currency` when the row was restated into the
      // company's on the comparison page (`allocationCurrency.ts`), else `currency` itself.
      po_currency: cheapest.quoted_currency ?? cheapest.currency,
      // The supplier's own figure. `rate` is the restated one and belongs to costing, not the PO.
      po_currency_rate: poRate,
      ...(cheapest.supplier_quotation ? { supplier_quotation: cheapest.supplier_quotation } : {}),
      // `po_currency_rate × conversion_rate = cost_unit`. `quoteCost` is in the quotation's
      // currency and `quoted_rate` is in the supplier's, so their ratio IS the rate — derived
      // rather than assumed 1, which is what made the comparison read $50 as AED 50. Not rounded:
      // a rate rounded to 2dp no longer reproduces the cost it was derived from.
      // `quoted_rate` is absent when nothing was restated, in which case `rate` IS the
      // supplier's figure — the same fallback `po_currency_rate` above takes.
      ...(poRate > 0 ? { conversion_rate: roundRate(quoteCost / poRate) } : {}),
    })
  }

  const netTotal = round2(items.reduce((sum, i) => sum + i.amount, 0))

  return {
    ok: true,
    conversion: {
      from: input.baseCurrency,
      to: quoteCurrency,
      rate: picked.rate,
      note: describeRate(picked, input.today),
    },
    payload: {
      status: 'Draft',
      revision: 0,
      enquiry: input.enquiryId,
      customer: input.customer,
      customer_name: input.customerName,
      transaction_date: input.transactionDate,
      customer_currency: quoteCurrency,
      exchange_rate: picked.rate,
      supplier_quote_comparison: input.comparisonName,
      sales_person: input.salesPerson,
      purchaser: input.purchaser,
      vessel: input.vessel,
      delivery_location: input.deliveryLocation,
      ...(input.deliveryPort ? { port: input.deliveryPort } : {}),
      /*
        ZERO, deliberately, and not "no markup".

        The server multiplies the document total by `(1 + markup/100)`. Our line `sell_unit` and
        `amount` ALREADY carry the margin, so a header markup charges the customer for it twice.
        Measured 2026-08-08 on QTN-2026-00004: sent `net_total 210.45` with `markup 15`, the server
        stored `grand_total 242.0175` — exactly 210.45 x 1.15. Setting it to 0 stored 210.45.

        The margin actually applied is not lost: every line carries `margin` and
        `default_margin_pct`, which is what the quote screen reads and edits.
      */
      markup: 0,
      // Commissions are NOT set here. They are per-document decisions a salesperson makes on the
      // quote screen, and defaulting them to 0 in a created document is different from leaving
      // them for that screen to fill — which `update_quotation` already does.
      net_total: netTotal,
      total: netTotal,
      grand_total: netTotal,
      items,
    },
  }
}

/** The money a caller can show before creating anything. */
export function payloadTotal(payload: CreateQuotationPayload, currency: string): Money {
  return { amount: payload.grand_total, currency }
}
